"use client";

import { useState } from "react";

/** Convert 24h "HH:MM" to { hours12, minutes, period } */
function to12h(time24: string): { hours: string; minutes: string; period: "AM" | "PM" } {
  const [hStr, mStr] = time24.split(":");
  let h = parseInt(hStr, 10);
  if (isNaN(h)) return { hours: "", minutes: mStr || "", period: "PM" };
  const period: "AM" | "PM" = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return { hours: String(h), minutes: mStr || "00", period };
}

/** Convert 12h back to "HH:MM" 24h format. Only pads when digits are complete. */
function to24h(hours: string, minutes: string, period: "AM" | "PM"): string {
  let h = parseInt(hours, 10);
  if (isNaN(h)) return "";
  const m = minutes || "00";
  if (period === "AM" && h === 12) h = 0;
  else if (period === "PM" && h !== 12) h += 12;
  return `${String(h).padStart(2, "0")}:${m.padStart(2, "0")}`;
}

interface TimeInputProps {
  value: string; // 24h "HH:MM" or ""
  onChange: (value24h: string) => void;
  className?: string;
}

export function TimeInput({ value, onChange, className = "" }: TimeInputProps) {
  // Derive display values from prop; local overrides used while typing
  const derived = value ? to12h(value) : { hours: "", minutes: "", period: "PM" as const };
  const [localHours, setLocalHours] = useState<string | null>(null);
  const [localMinutes, setLocalMinutes] = useState<string | null>(null);
  const [period, setPeriod] = useState<"AM" | "PM">(derived.period);
  const [prevValue, setPrevValue] = useState(value);

  // Reset when external value changes
  if (value !== prevValue) {
    setPrevValue(value);
    const p = value ? to12h(value) : { hours: "", minutes: "", period: "PM" as const };
    if (p.period !== period) setPeriod(p.period);
    if (localHours !== null) setLocalHours(null);
    if (localMinutes !== null) setLocalMinutes(null);
  }

  const hours = localHours ?? derived.hours;
  const minutes = localMinutes ?? derived.minutes;

  const emit = (h: string, m: string, p: "AM" | "PM") => {
    if (h) {
      onChange(to24h(h, m, p));
    } else {
      onChange("");
    }
  };

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <input
        type="text"
        value={hours}
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, "").slice(0, 2);
          setLocalHours(v);
          emit(v, minutes, period);
        }}
        placeholder="12"
        maxLength={2}
        className="w-8 rounded border border-neutral-700 bg-dark px-1.5 py-1 text-center font-mono text-[11px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50"
      />
      <span className="font-mono text-[10px] text-neutral-600">:</span>
      <input
        type="text"
        value={minutes}
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, "").slice(0, 2);
          setLocalMinutes(v);
          emit(hours, v, period);
        }}
        placeholder="00"
        maxLength={2}
        className="w-8 rounded border border-neutral-700 bg-dark px-1.5 py-1 text-center font-mono text-[11px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50"
      />
      <div className="flex rounded border border-neutral-700 overflow-hidden">
        <button
          type="button"
          onClick={() => { setPeriod("AM"); emit(hours, minutes, "AM"); }}
          className={`px-1.5 py-1 font-mono text-[10px] font-bold transition-colors ${
            period === "AM"
              ? "bg-acid/20 text-acid"
              : "bg-dark text-neutral-600 hover:text-neutral-400"
          }`}
        >
          AM
        </button>
        <button
          type="button"
          onClick={() => { setPeriod("PM"); emit(hours, minutes, "PM"); }}
          className={`px-1.5 py-1 font-mono text-[10px] font-bold transition-colors ${
            period === "PM"
              ? "bg-acid/20 text-acid"
              : "bg-dark text-neutral-600 hover:text-neutral-400"
          }`}
        >
          PM
        </button>
      </div>
    </div>
  );
}
